import { DEFAULT_MEMORY_SIZE, isMemorySize, type MemorySize } from '@hazel/shared'

/**
 * One board, one table.
 *
 * The arrow-key cursor used to keep its own idea of how wide the grid was —
 * four, for every board — while the CSS quietly widened to six or eight at the
 * `sm` breakpoint. Down then moved four cells across a six-wide row, which is
 * a diagonal, and it looked like a broken cursor rather than a mismatch.
 *
 * So the number of columns and the class that draws them live together, and a
 * test checks they still agree. The class strings stay written out in full
 * because Tailwind scans the source for them; a template string would compile
 * to nothing.
 */
export interface MemoryLayout {
  /** Columns below the `sm` breakpoint, and at or above it. */
  readonly narrow: number
  readonly wide: number
  readonly className: string
  /**
   * The same board on the television, where the height is the scarce thing.
   *
   * Carries its own `data-scale=tv` prefixes rather than being prefixed where
   * it is used: half of one of these classes reached the phone that way, and
   * a six-wide board on a 390px screen is a 46px card.
   *
   * Both axes in `1fr`, and the board carries the shape of its own grid as an
   * aspect ratio. Stretched to the height it is given, it is then exactly as
   * wide as that shape asks for — a tight block in the middle of the screen
   * rather than five columns strewn across 1830px, which is what full-width
   * `1fr` columns give you. Always the wide column count: a television is
   * never narrow.
   *
   * True of every board but the widest: eight columns over two rows asks for
   * more width than a 1920 screen has, so that one meets `max-w-full` first
   * and its box comes out at 3.95 rather than the 4 declared here. The cards
   * stay square through it — each takes its height from its own width — and
   * the board still fits. Worth knowing before trusting the ratio as a
   * measurement rather than as a request.
   *
   * The gap is nil here and lives on the cards instead. A gap on the grid is
   * subtracted `(cols-1)` times across and `(rows-1)` times down, so on any
   * board that is not square the cells come out slightly oblong and the cards
   * with them; carried as a margin it comes off both axes equally and they
   * stay square.
   */
  readonly tvClassName: string
  /**
   * The same treatment, asked for by the height of the screen rather than by
   * the scale it is being read at.
   *
   * The room's board is always at television scale — `TvLayout` sets it
   * whatever the device — so the scale says nothing there about whether the
   * screen has height to divide. A phone hosting had 844 pixels, most of them
   * spent on a bar, a legend and three scores, and this treatment faithfully
   * produced a sixteen-pixel card (DOL-107).
   *
   * Written out rather than derived from the string above, because Tailwind
   * generates what it can see: a class assembled at runtime is a class that
   * was never built.
   */
  readonly tallClassName: string
}

export const MEMORY_LAYOUT: Readonly<Record<MemorySize, MemoryLayout>> = {
  8: {
    narrow: 4,
    wide: 4,
    className: 'grid-cols-4',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(4,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(2,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-[4/2]',
    tallClassName:
      '[@media(min-height:900px)]:grid-cols-[repeat(4,minmax(0,1fr))] [@media(min-height:900px)]:grid-rows-[repeat(2,minmax(0,1fr))] [@media(min-height:900px)]:aspect-[4/2]',
  },
  12: {
    narrow: 4,
    wide: 6,
    className: 'grid-cols-4 sm:grid-cols-6',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(6,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(2,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-[6/2]',
    tallClassName:
      '[@media(min-height:900px)]:grid-cols-[repeat(6,minmax(0,1fr))] [@media(min-height:900px)]:grid-rows-[repeat(2,minmax(0,1fr))] [@media(min-height:900px)]:aspect-[6/2]',
  },
  16: {
    narrow: 4,
    wide: 8,
    className: 'grid-cols-4 sm:grid-cols-8',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(8,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(2,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-[8/2]',
    tallClassName:
      '[@media(min-height:900px)]:grid-cols-[repeat(8,minmax(0,1fr))] [@media(min-height:900px)]:grid-rows-[repeat(2,minmax(0,1fr))] [@media(min-height:900px)]:aspect-[8/2]',
  },
  20: {
    narrow: 4,
    wide: 5,
    className: 'grid-cols-4 sm:grid-cols-5',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(5,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(4,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-[5/4]',
    tallClassName:
      '[@media(min-height:900px)]:grid-cols-[repeat(5,minmax(0,1fr))] [@media(min-height:900px)]:grid-rows-[repeat(4,minmax(0,1fr))] [@media(min-height:900px)]:aspect-[5/4]',
  },
  /*
   * The big boards, and all of them four rows.
   *
   * The television's board is bound by the height it is given, so rows are
   * what decide the size of a card and columns are free. Holding four and
   * growing sideways means forty cards are drawn exactly as large as twenty —
   * 104px either way, measured — into width a 16:9 screen has going spare.
   *
   * A fifth row would be 81px, under the floor the television test holds, so
   * this is the shape rather than a preference.
   */
  24: {
    narrow: 4,
    wide: 6,
    className: 'grid-cols-4 sm:grid-cols-6',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(6,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(4,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-[6/4]',
    tallClassName:
      '[@media(min-height:900px)]:grid-cols-[repeat(6,minmax(0,1fr))] [@media(min-height:900px)]:grid-rows-[repeat(4,minmax(0,1fr))] [@media(min-height:900px)]:aspect-[6/4]',
  },
  32: {
    narrow: 4,
    wide: 8,
    className: 'grid-cols-4 sm:grid-cols-8',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(8,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(4,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-[8/4]',
    tallClassName:
      '[@media(min-height:900px)]:grid-cols-[repeat(8,minmax(0,1fr))] [@media(min-height:900px)]:grid-rows-[repeat(4,minmax(0,1fr))] [@media(min-height:900px)]:aspect-[8/4]',
  },
  40: {
    narrow: 4,
    wide: 10,
    className: 'grid-cols-4 sm:grid-cols-10',
    tvClassName:
      '[[data-scale=tv]_&]:grid-cols-[repeat(10,minmax(0,1fr))] [[data-scale=tv]_&]:grid-rows-[repeat(4,minmax(0,1fr))] [[data-scale=tv]_&]:aspect-[10/4]',
    tallClassName:
      '[@media(min-height:900px)]:grid-cols-[repeat(10,minmax(0,1fr))] [@media(min-height:900px)]:grid-rows-[repeat(4,minmax(0,1fr))] [@media(min-height:900px)]:aspect-[10/4]',
  },
}

/** Tailwind's `sm`, which is where every one of these boards changes shape. */
export const WIDE_FROM = '(min-width: 40rem)'

/** How many columns are actually on screen for this board. */
export function columnsFor(size: MemorySize, wide: boolean): number {
  const layout = MEMORY_LAYOUT[size]
  return wide ? layout.wide : layout.narrow
}

/**
 * How many rows that board draws on a wide screen.
 *
 * Derived, never written down: a written-down row count is a second copy of
 * the same fact, and the diagonal cursor above is what happens when two copies
 * of "how wide is the grid" drift apart. Every board divides exactly — a test
 * holds that, because a board that did not would round the television's height
 * cap to a fraction of a card.
 */
export function rowsWhenWide(size: MemorySize): number {
  return size / MEMORY_LAYOUT[size].wide
}

/**
 * How to draw a board when all you have is the cards on it.
 *
 * The host display has no round config and no picker — it is handed a view and
 * a list of cards — so it used to work the shape out for itself:
 *
 *     const columns = game.cards.length > 12 ? 'grid-cols-5' : 'grid-cols-4'
 *
 * That was a **third** answer to "how many columns has a memory board", living
 * apart from the two above and checked against neither. It agreed with them at
 * twenty cards, which is the only size a room can currently deal, and disagreed
 * at every other: four columns where the table says six at twelve, five where
 * the table says six, eight and ten on the boards added since.
 *
 * The moment a room gets a size picker the television would have drawn a shape
 * the game does not have — quietly, with no cursor to go diagonal and give it
 * away. So the answer lives here with the others, and a test holds all three
 * level.
 *
 * The everyday class is the right one to hand back even though the host is
 * always a television: it carries the narrow count *and* the wide one, and a
 * host is never narrow, so the wide count is what it draws.
 */
/**
 * The television half of the same table.
 *
 * `boardClassName` gives the columns; this gives the treatment that makes a
 * board fit the screen rather than its own contents — rows, and an aspect that
 * keeps the cards square while `flex-1` decides how much height they get.
 *
 * The solo board has always composed both. The room's board took only the
 * first, which is why twenty cards ran past a 1080 screen with three people
 * seated (DOL-94, and the part of it that only shows up with a full room).
 */
export function tvBoardClassName(cards: number): string {
  const size = isMemorySize(cards) ? cards : DEFAULT_MEMORY_SIZE

  return MEMORY_LAYOUT[size].tallClassName
}

export function boardClassName(cards: number): string {
  // A count that is not a board size is a programming error rather than
  // something a television should crash on mid-game. It falls back to the only
  // size a room can deal, which is also the size it would have been.
  const size = isMemorySize(cards) ? cards : DEFAULT_MEMORY_SIZE

  return MEMORY_LAYOUT[size].className
}
